/**
 * Utility functions for token calculation
 */

/**
 * Model-specific tokenizer configurations
 * Different models have different tokenization strategies
 */
const MODEL_TOKEN_CONFIGS = {
  // OpenAI models (GPT-3.5, GPT-4)
  'gpt-3.5-turbo': { koreanRatio: 2.5, englishRatio: 4, overhead: 1.1 },
  'gpt-4': { koreanRatio: 2.5, englishRatio: 4, overhead: 1.1 },
  'gpt-4-turbo': { koreanRatio: 2.5, englishRatio: 4, overhead: 1.1 },
  'gpt-4o': { koreanRatio: 2.3, englishRatio: 3.8, overhead: 1.1 },
  'gpt-4o-mini': { koreanRatio: 2.3, englishRatio: 3.8, overhead: 1.1 },
  
  // Anthropic models (Claude)
  'claude-3-opus': { koreanRatio: 2.2, englishRatio: 3.5, overhead: 1.15 },
  'claude-3-5-sonnet': { koreanRatio: 2.2, englishRatio: 3.5, overhead: 1.15 },
  'claude-3-5-haiku': { koreanRatio: 2.2, englishRatio: 3.5, overhead: 1.15 },
  'claude-opus-4': { koreanRatio: 2.0, englishRatio: 3.3, overhead: 1.2 }, // Thinking models need more overhead
  
  // Google models (Gemini)
  'gemini-2.5-pro': { koreanRatio: 2.8, englishRatio: 4.2, overhead: 1.05 },
  'gemini-2.5-flash': { koreanRatio: 2.8, englishRatio: 4.2, overhead: 1.05 },
  'gemini-2.5-flash-lite': { koreanRatio: 3.0, englishRatio: 4.5, overhead: 1.0 },
  
  // Upstage models
  'solar-mini': { koreanRatio: 2.0, englishRatio: 3.5, overhead: 1.1 },
  'solar-pro': { koreanRatio: 2.0, englishRatio: 3.5, overhead: 1.1 },
  
  // OpenRouter/Free models
  'gpt-oss-120b': { koreanRatio: 2.5, englishRatio: 4.0, overhead: 1.0 },
  'qwen3-32b': { koreanRatio: 2.3, englishRatio: 3.8, overhead: 1.0 },
  
  // Default configuration
  'default': { koreanRatio: 2.5, englishRatio: 4.0, overhead: 1.1 }
}

/**
 * Get token configuration for a specific model
 */
function getModelTokenConfig(modelName: string) {
  // Try exact match first
  if (MODEL_TOKEN_CONFIGS[modelName as keyof typeof MODEL_TOKEN_CONFIGS]) {
    return MODEL_TOKEN_CONFIGS[modelName as keyof typeof MODEL_TOKEN_CONFIGS]
  }
  
  // Try partial match (e.g., "gpt-4-0125-preview" matches "gpt-4")
  for (const [key, config] of Object.entries(MODEL_TOKEN_CONFIGS)) {
    if (modelName.includes(key)) {
      return config
    }
  }
  
  // Return default if no match
  return MODEL_TOKEN_CONFIGS.default
}

/**
 * Estimate token count for text with model-specific weights
 */
export function estimateTokenCount(text: string, modelName?: string): number {
  if (!text) return 0
  
  const config = getModelTokenConfig(modelName || 'default')
  
  // Count Korean characters (Hangul, Hanja)
  const koreanChars = (text.match(/[\u3131-\uD79D\uAC00-\uD7AF]/g) || []).length
  // Count non-Korean characters
  const nonKoreanChars = text.length - koreanChars
  
  // Calculate base tokens with model-specific ratios
  const baseTokens = Math.ceil(
    koreanChars / config.koreanRatio + 
    nonKoreanChars / config.englishRatio
  )
  
  // Apply model overhead (for special tokens, formatting, etc.)
  const estimatedTokens = Math.ceil(baseTokens * config.overhead)
  
  return estimatedTokens
}

/**
 * Calculate max tokens needed for AI response based on input
 * Takes into account the model and task type
 */
export function calculateMaxTokens(inputText: string, modelName?: string): number {
  const inputTokens = estimateTokenCount(inputText, modelName)
  
  // Model-specific limits
  const MODEL_LIMITS: Record<string, { min: number; max: number; default: number }> = {
    'gpt-4': { min: 500, max: 4096, default: 2000 },
    'gpt-4-turbo': { min: 500, max: 4096, default: 2000 },
    'gpt-4o': { min: 500, max: 4096, default: 2000 },
    'gpt-3.5-turbo': { min: 500, max: 4096, default: 2000 },
    'claude-3-opus': { min: 500, max: 4096, default: 2500 },
    'claude-3-5-sonnet': { min: 500, max: 4096, default: 2500 },
    'claude-opus-4': { min: 1000, max: 15000, default: 8000 }, // Thinking models
    'gemini-2.5-pro': { min: 500, max: 8192, default: 3000 },
    'gemini-2.5-flash': { min: 500, max: 8192, default: 2500 },
    'default': { min: 500, max: 4096, default: 2000 }
  }
  
  // Get model-specific limits
  let limits = MODEL_LIMITS.default
  for (const [key, value] of Object.entries(MODEL_LIMITS)) {
    if (modelName?.includes(key)) {
      limits = value
      break
    }
  }
  
  // For grammar checking, we need detailed analysis
  // Response should be at least 2-3x the input for proper explanation
  let responseMultiplier = 2.5
  
  // For very short texts, use default
  if (inputTokens < 50) {
    return limits.default
  }
  
  // For thinking models, allow more tokens
  if (modelName?.includes('opus-4') || modelName?.includes('o1')) {
    responseMultiplier = 4.0
  }
  
  // Calculate needed tokens
  const calculatedTokens = Math.ceil(inputTokens * responseMultiplier)
  
  // Apply limits
  return Math.max(limits.min, Math.min(calculatedTokens, limits.max))
}

/**
 * Get default temperature value
 */
export function getDefaultTemperature(): number {
  return 0.3
}